// @ts-nocheck
import { Alert, AlertIcon, type AlertProps, Box, CloseButton, HStack, Text } from "@chakra-ui/react";
import type React from "react";
import { useNavigate } from "react-router";

type StatusType = "warning" | "success" | "info" | "error";

const statusColors: Record<StatusType, string> = {
  warning: "equidefi.yellow",
  success: "equidefi.green",
  info: "equidefi.blue",
  error: "equidefi.red",
};

/**
 * Helper function to render paragraphs from string content split by double newlines
 */
const renderStringDescription = (text: string, textStyle: string, textTransform: string, margin?: string) => {
  const paragraphs = text.split("\n\n");
  return (
    <Text as="span" textStyle={textStyle} m={margin} textTransform={textTransform}>
      {paragraphs.map((paragraph: string, index: number) =>
        paragraph ? (
          <Text as="p" key={`${paragraph.slice(0, 10)}-${index}`} mb={index < paragraphs.length - 1 ? 4 : 0}>
            {paragraph}
          </Text>
        ) : null,
      )}
    </Text>
  );
};

interface BannerAlertProps extends AlertProps {
  /** Alert status type that determines color scheme */
  status?: StatusType;
  /** Optional title text */
  title?: string;
  /**
   * Description content - can be a string or React component.
   * When string: automatically splits on double newlines (\n\n) into separate paragraphs.
   * When ReactNode: renders as-is without any text processing.
   */
  description?: string | React.ReactNode | null;
  /** Optional link target - makes the entire banner clickable */
  linkTarget?: string | null;
  /** Whether to hide the alert icon */
  hideIcon?: boolean | false;
  /** Whether to center-align text content */
  centered?: boolean | false;
  /** Whether to uppercase text content */
  uppercase?: boolean | false;
  /**
   * Layout style for title and icon arrangement:
   * - "stacked": Icon on left, title and description stacked vertically (default when no title)
   * - "inline": Icon and title side-by-side in a row, description below (default when title exists)
   * - "column": No icon, title and description stacked vertically
   */
  titleLayout?: "stacked" | "inline" | "column";
  /** When provided, renders a close button that invokes this handler. */
  onClose?: () => void;
}

export const BannerAlert = ({
  status = "warning",
  title,
  description,
  linkTarget = null,
  hideIcon = false,
  centered = false,
  uppercase = false,
  titleLayout,
  onClose,
  ...props
}: BannerAlertProps) => {
  // Default layout logic: inline when title exists, stacked when no title
  const effectiveLayout = titleLayout || (title ? "inline" : "stacked");
  // Safely handle useNavigate hook to avoid Router context errors
  let navigate = null;
  try {
    navigate = useNavigate();
  } catch (error) {
    // Handle case where component is used outside Router context
    if (linkTarget) {
      console.warn("BannerAlert: linkTarget provided but component is not wrapped in a Router context");
    }
  }

  const alert = (
    <Alert
      status={status}
      borderRadius="lg"
      border="1px solid"
      borderColor={statusColors[status]}
      boxShadow="sm"
      flexDirection={effectiveLayout === "column" ? "column" : "row"}
      alignItems={effectiveLayout === "column" ? (centered ? "center" : "start") : "center"}
      justifyContent={centered ? "center" : "flex-start"}
      position={onClose ? "relative" : undefined}
      pr={onClose ? 10 : undefined}
      {...props}>
      {effectiveLayout === "stacked" && !hideIcon && <AlertIcon />}
      <Box
        textAlign={centered ? "center" : "left"}
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems={centered ? "center" : "flex-start"}>
        {effectiveLayout === "column" ? (
          <Box>
            {title && (
              <Text
                as="span"
                textStyle="h3"
                fontWeight="bold"
                display="block"
                mb={2}
                textTransform={uppercase ? "uppercase" : "none"}>
                {title}
              </Text>
            )}
            {description &&
              (typeof description === "string" ? (
                renderStringDescription(description, "body2", uppercase ? "uppercase" : "none", "0")
              ) : (
                <Box>{description}</Box>
              ))}
          </Box>
        ) : effectiveLayout === "inline" && title ? (
          <Box w="full">
            <HStack gap="0" mb="2" justifyContent={centered ? "center" : "flex-start"} alignItems="center">
              {!hideIcon && <AlertIcon />}
              <Text as="span" textStyle="h3" fontWeight="bold" textTransform={uppercase ? "uppercase" : "none"}>
                {title}
              </Text>
            </HStack>
            {description &&
              (typeof description === "string" ? (
                renderStringDescription(description, "body2", uppercase ? "uppercase" : "none", "0")
              ) : (
                <Box>{description}</Box>
              ))}
          </Box>
        ) : (
          <>
            {title && (
              <Text
                as="span"
                textStyle="body2"
                fontWeight="bold"
                display="block"
                textTransform={uppercase ? "uppercase" : "none"}>
                {title}
              </Text>
            )}
            {description &&
              (typeof description === "string" ? (
                renderStringDescription(description, "body2", uppercase ? "uppercase" : "none")
              ) : (
                <Box>{description}</Box>
              ))}
          </>
        )}
      </Box>
      {onClose && (
        <CloseButton
          position="absolute"
          top="2"
          right="2"
          size="sm"
          type="button"
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
      )}
    </Alert>
  );

  if (linkTarget && navigate) {
    return (
      <Box onClick={() => navigate(linkTarget)} cursor="pointer" _hover={{ opacity: 0.8 }} transition="opacity 0.2s">
        {alert}
      </Box>
    );
  }

  return alert;
};
